/**
 * AB#35119 (reopened): Quick Entry must SAVE a requestor contact when the specialist sets it, not
 * defer every contact write to Confirm All.
 *
 * Why this exists at all. The server owns rules that a specialist can only fix by editing the field
 * they just left - the blacklisted email domain
 * (`dbo.tblRequestorEmailDomainBlackList` -> `RequestorController.UpdateSendToRequestorContact`) is the
 * reported case. Classic auto-saves contact details on blur, so that rule fires against the field
 * that caused it. Quick Entry's Edit-requestor and assign-existing-contact paths were form-only, so
 * the same input sailed through until Confirm All, where the refusal arrives detached from its cause.
 * The first attempt at this bug (PR !9605) only improved the WORDING of a failure Quick Entry had
 * already attempted; it did not change when Quick Entry saves, which is why the bug came back.
 *
 * This module is the pure part - the request parameters - kept separate from the component so it can
 * be unit-tested without DataEntryDetails' node:stream-tainted import chain (same rationale as
 * intakeFetchGate.ts / intakeAutoTrigger.ts / intakeAnalysisOutcome.ts).
 *
 * THE ONE THING NOT TO BREAK: `transactionId`. The blacklist check on that endpoint sits inside
 * `if (!string.IsNullOrEmpty(transactionId))`, so a PATCH without it updates the contact and skips
 * validation entirely - the save would appear to succeed and the bug would be back with no visible
 * symptom. `buildSendToContactPatchParams` therefore refuses to build params without one, and
 * `sendToContactPatchQuery` is the only place that serialises them.
 */

/** Field names as the endpoint's query string expects them (mirrors generateSendToRequestorContactObject). */
export interface SendToContactPatchParams {
    readonly requestorcontactid: number;
    readonly transactionId: string;
    readonly emailconfirmed: false;
    readonly firstname?: string;
    readonly lastname?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly fax?: string;
    /** Classic passes the transaction's request type; the server skips verification email for type 31. */
    readonly requestType?: string;
}

export interface SendToContactValues {
    readonly contactFirstName?: string;
    readonly contactLastName?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly fax?: string;
}

/**
 * Builds the PATCH parameters, or `undefined` when the save must not be attempted.
 *
 * Returns undefined - rather than throwing - for the two states that are normal rather than
 * exceptional: no contact id yet (the contact isn't in the database, so there is nothing to PATCH;
 * Confirm All's create path owns that case) and no transaction id (see the header - a save without
 * one is worse than no save, because it silently skips validation).
 *
 * Empty strings are dropped to `undefined` so a blank field is omitted from the query rather than
 * sent as an empty value, matching generateSendToRequestorContactObject's `|| undefined`.
 */
export function buildSendToContactPatchParams(
    contactId: number | undefined | null,
    transactionId: string | undefined | null,
    values: SendToContactValues,
    requestType?: number | string | null,
): SendToContactPatchParams | undefined {
    if (contactId == null || !transactionId) {
        return undefined;
    }
    const clean = (value?: string) => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
    };
    return {
        requestorcontactid: contactId,
        transactionId,
        emailconfirmed: false,
        firstname: clean(values.contactFirstName),
        lastname: clean(values.contactLastName),
        email: clean(values.email),
        phone: clean(values.phone),
        fax: clean(values.fax),
        requestType: requestType == null || requestType === "" ? undefined : String(requestType),
    };
}

/**
 * Serialises params into the endpoint's query string.
 *
 * `requestorcontactid` is deliberately excluded - it is a PATH segment on this endpoint
 * (`/api/requestor/{requestorcontactid}/updatesendtorequestorcontact`), and Classic's own client
 * deletes it from the query for exactly that reason. Undefined values are omitted entirely.
 */
export function sendToContactPatchQuery(params: SendToContactPatchParams): string {
    const pairs: string[] = [];
    for (const [key, value] of Object.entries(params)) {
        if (key === "requestorcontactid" || value === undefined || value === null) {
            continue;
        }
        pairs.push(`${key}=${encodeURIComponent(String(value))}`);
    }
    return pairs.join("&");
}

/** Full relative URL for the contact PATCH, including the userID the endpoint expects. */
export function sendToContactPatchUrl(params: SendToContactPatchParams, userId: number | string): string {
    return `requestor/${params.requestorcontactid}/updatesendtorequestorcontact`
        + `?userID=${encodeURIComponent(String(userId))}&${sendToContactPatchQuery(params)}`;
}
